import { Box, Button, SxProps, styled } from '@mui/material';
import {
  faCheck,
  faCheckDouble,
  faTimes
} from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import { DARK, GRAY, GRAY_LIGHT, PRIMARY, RED, WHITE } from 'constants/colors';
import { MessageAction, MessageActionType } from '../types';
import { useTranslation } from 'react-i18next';
import useUpdateEmployeeMessageTask from '../hooks/useUpdateEmployeeMessageTask';
import { useQueryClient } from 'react-query';

const ActionButton = styled(Button)`
  min-width: 60px;
  height: 36px;
  padding: 8px;
  box-sizing: border-box;
  background-color: ${GRAY_LIGHT};
  border-radius: 4px;
  color: ${DARK};

  svg {
    margin-right: 8px;
  }

  &:hover {
    background-color: ${GRAY};
  }
`;

type ActionsButtonsProps = {
  actions: MessageAction[];
  taskId: string | number;
  sx?: SxProps;
  isRejectedDuty?: boolean;
};

const ActionsButtons = (props: ActionsButtonsProps) => {
  const { actions = [], taskId, sx, isRejectedDuty } = props;

  const availableActionTypes = actions.flatMap((action) =>
    Object.values(action)
  );

  const { t } = useTranslation('common', {
    keyPrefix: 'employeeMessages.actions'
  });
  const { mutateAsync: updateAction } = useUpdateEmployeeMessageTask();
  const queryClient = useQueryClient();

  const handleAction = async (
    event: React.MouseEvent,
    taskId: string | number,
    actionType: MessageActionType
  ) => {
    event.preventDefault();
    event.stopPropagation();

    await updateAction({ taskId, actionType });
    queryClient.invalidateQueries('employeeMessages');
  };

  return (
    <Box display="flex" gap={1} sx={sx || {}}>
      {availableActionTypes.map((type: MessageActionType, i: number) => (
        <ActionButton
          key={i}
          onClick={(event: React.MouseEvent) =>
            handleAction(event, taskId, type)
          }
          sx={{
            bgcolor: isRejectedDuty ? WHITE : GRAY_LIGHT
          }}
        >
          {(() => {
            switch (type) {
              case MessageActionType.ACCEPT:
              case MessageActionType.AVAILABLE:
                return (
                  <FontAwesomeSvgIcon
                    icon={faCheck}
                    sx={{ color: PRIMARY, fontSize: '14px' }}
                  />
                );
              case MessageActionType.SEEN:
                return (
                  <FontAwesomeSvgIcon
                    icon={faCheckDouble}
                    sx={{ color: DARK, fontSize: '14px' }}
                  />
                );
              case MessageActionType.NOT_AVAILABLE:
              case MessageActionType.REJECT:
                return (
                  <FontAwesomeSvgIcon
                    icon={faTimes}
                    sx={{ color: RED, fontSize: '14px' }}
                  />
                );
              default:
                return null;
            }
          })()}
          {t(type)}
        </ActionButton>
      ))}
    </Box>
  );
};

export default ActionsButtons;
